// How the Sign-in column reads a row.
//
// The column answers "can this person use Maestro, and if not what do I do" -
// not "did we email them". Those came apart badly in the first version: most
// Han Staff accounts arrive by ATS sync rather than through an invitation, so
// 50 of the 102 listed users have no invite on record, and the column told a
// colleague who signs in every day that they had never been invited.
//
// Sign-in is the fact that settles it. Somebody who has signed in can use
// Maestro, however they came by their password, and nothing about invitations
// is worth saying on their row. Only where they have never got in does the
// invitation explain why, and only then is there anything for an admin to do.
//
// These live in their own module, free of mobx and of every store, because two
// screens now read them: User Roles (internal users, `employer.user`) and
// Candidate App Access (candidates, `worker.user`). Both sides of the `user`
// record carry the same three timestamps, so the same four states describe
// both.
export const ACCESS_STATE = {
	signedIn: 'signedIn',
	// Invited, not yet used, still time on the temporary password.
	awaiting: 'awaiting',
	// Invited, temporary password lapsed unused - they cannot get in.
	expired: 'expired',
	// Never invited and never signed in - no route in at all.
	notInvited: 'notInvited',
}

export const getAccessState = (user) => {
	if (user?.lastSignInAt) return ACCESS_STATE.signedIn
	if (!user?.welcomeEmailSentAt) return ACCESS_STATE.notInvited

	const expiresAt = user?.tempPasswordExpiredAt
	if (expiresAt && new Date(expiresAt) < new Date()) return ACCESS_STATE.expired

	return ACCESS_STATE.awaiting
}

// Only the two states where someone truly cannot sign in carry colour. Everything
// else is muted, so a red row means something rather than being half the table.
export const isAccessBlocked = (user) => {
	const state = getAccessState(user)

	return state === ACCESS_STATE.expired || state === ACCESS_STATE.notInvited
}

// Whether an admin should see Send/Resend invite. Broader than isAccessBlocked:
// someone in `awaiting` can still sign in with their current temporary password,
// but admins need a path when that email was never received — without waiting for
// expiry or calling support.
export const canAdminSendInvite = (user) => {
	const state = getAccessState(user)

	return state === ACCESS_STATE.expired || state === ACCESS_STATE.notInvited || state === ACCESS_STATE.awaiting
}

// After assigning a role, only offer Send/Resend invite when the person still cannot
// sign in on their own. Signed-in colleagues already have access — skip the prompt.
export const shouldPromptInviteAfterRoleAssign = (user) => canAdminSendInvite(user)
